import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
} from "react";

import {
  useParams,
  useNavigate,
  Link,
} from "react-router-dom";

import API from "../api/api";

import { AuthContext } from "../context/AuthContext";


// ==========================================================
// ADMIN CREATE / EDIT BRACKET
// ==========================================================

export default function AdminCreateBracket() {
  const params =
    useParams();

  const tournamentId =
    params.id ||
    params.tournamentId;

  const navigate =
    useNavigate();

  const {
    user,
  } = useContext(AuthContext);

  const isAdmin =
    user?.role === "admin";

  // ========================================================
  // STATE
  // ========================================================

const [resetting, setResetting] = useState(false);
  const [
    allFighters,
    setAllFighters,
  ] = useState([]);

  const [
    bracketCategories,
    setBracketCategories,
  ] = useState([]);

  const [
    savedBrackets,
    setSavedBrackets,
  ] = useState({});

  const [
    bracketRounds,
    setBracketRounds,
  ] = useState([]);

  const [
    selectedCategory,
    setSelectedCategory,
  ] = useState("");

  const [
    selectedBracketId,
    setSelectedBracketId,
  ] = useState(null);

  const [
    selectedFighter,
    setSelectedFighter,
  ] = useState(null);

  const [
    moveDestination,
    setMoveDestination,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    generating,
    setGenerating,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    successMsg,
    setSuccessMsg,
  ] = useState("");

  const [
    filterType,
    setFilterType,
  ] = useState("");

  const [
    filterGender,
    setFilterGender,
  ] = useState("");

  const [
    filterAge,
    setFilterAge,
  ] = useState("");

  const [
    filterWeight,
    setFilterWeight,
  ] = useState("");

  const [
    sortMode,
    setSortMode,
  ] = useState("default");

  const [
    fighterSearch,
    setFighterSearch,
  ] = useState("");

  const [
    hasUnsavedChanges,
    setHasUnsavedChanges,
  ] = useState(false);

  const resetBrackets = async () => {
  const confirmed = window.confirm(
    "⚠️ Are you sure?\n\nThis will delete ALL generated brackets, matches, winners, and fight schedules for this tournament.\n\nRegistrations will NOT be deleted."
  );

  if (!confirmed) return;

  try {
    setResetting(true);

    await API.post(
      `/tournaments/${tournamentId}/brackets/reset`
    );

    alert(
      "Brackets reset successfully. You can now generate new brackets."
    );

    // Refresh page data
    window.location.reload();

  } catch (err) {
    console.error(
      "Reset brackets error:",
      err?.response?.data || err
    );

    alert(
      err?.response?.data?.error ||
      "Failed to reset brackets."
    );

  } finally {
    setResetting(false);
  }
};

  // ========================================================
  // HELPERS
  // ========================================================

  const getFighterName = f => {
    if (!f) {
      return "BYE";
    }

    const full =
      `${f.name || ""} ${f.surname || ""}`.trim();

    if (full) {
      return full;
    }

    return (
      f.fighter_name ||
      f.full_name ||
      f.username ||
      `Fighter #${f.fighter_id || f.id || ""}`
    );
  };

  const normalizeFighter = f => {
  if (!f) return null;

  return {
    id:
      Number(
        f.fighter_id ??
        f.id
      ),

    registrationId:
      Number(
        f.registration_id ??
        f.registrationId ??
        f.id
      ),

    name:
      getFighterName(f),

    club:
      f.club_name ||
      f.club ||
      f.team_name ||
      "",

    clubLogo:
      f.club_logo ||
      f.club_logo_url ||
      "",

    imageUrl:
      f.image_url ||
      "",

    actualWeight:
      f.actual_weight != null
        ? Number(f.actual_weight)
        : null,

    registeredWeight:
      f.weight != null
        ? Number(f.weight)
        : null,
  };
};


  // ========================================================
  // CONVERT DB MATCHES → FRONTEND ROUNDS
  // ========================================================

  const convertMatchesToRounds =
    matches => {
      if (!matches?.length) {
        return [];
      }

      const maxRound =
        Math.max(
          ...matches.map(
            m => Number(m.round)
          )
        );

      const rounds =
        Array.from(
          {
            length: maxRound,
          },
          () => []
        );

      for (const m of matches) {
        const fighter1 =
          m.fighter1 ||
          null;

        const fighter2 =
          m.fighter2 ||
          null;

        rounds[
          Number(m.round) - 1
        ].push({
          match_id:
            m.match_id,

          match_number:
            Number(
              m.match_index
            ),

          round_number:
            Number(
              m.round
            ),

          fighter1_id:
            m.fighter1_id
              ? Number(
                  m.fighter1_id
                )
              : null,

          fighter1_name:
            m.fighter1_name ||
            (fighter1
              ? getFighterName(
                  fighter1
                )
              : "TBD"),

          fighter1_club:
            fighter1?.club_name ||
            "",

          fighter1_club_logo:
            fighter1?.club_logo ||
            "",

          fighter2_id:
            m.fighter2_id
              ? Number(
                  m.fighter2_id
                )
              : null,

          fighter2_name:
            m.fighter2_name ||
            (fighter2
              ? getFighterName(
                  fighter2
                )
              : "TBD"),

          fighter2_club:
            fighter2?.club_name ||
            "",

          fighter2_club_logo:
            fighter2?.club_logo ||
            "",

          winner_id:
            m.winner_id
              ? Number(
                  m.winner_id
                )
              : null,

          status:
            m.status ||
            "pending",
        });
      }

      rounds.forEach(
        round => {
          round.sort(
            (a, b) =>
              a.match_number -
              b.match_number
          );
        }
      );

      return rounds;
    };

  // ========================================================
  // LOAD REGISTRATIONS + CATEGORIES + EXISTING BRACKETS
  // ========================================================

  const loadData = async () => {
  if (!tournamentId) return;

  try {
    setLoading(true);
    setError("");

    // ----------------------------------------------------
    // LOAD REGISTRATIONS + CATEGORIES
    // ----------------------------------------------------

    const [
      registrationRes,
      categoriesRes,
    ] = await Promise.all([
      API.get(
        `/tournaments/${tournamentId}/registration`
      ),
      API.get(
        `/tournaments/${tournamentId}/bracket-categories`
      ),
    ]);

    // ----------------------------------------------------
// REGISTRATIONS
// ----------------------------------------------------

const rawRegistration =
  registrationRes?.data !== undefined
    ? registrationRes.data
    : registrationRes;

const rawFighters =
  Array.isArray(rawRegistration)
    ? rawRegistration
    : Array.isArray(
        rawRegistration?.fighters
      )
    ? rawRegistration.fighters
    : Array.isArray(
        rawRegistration?.data
      )
    ? rawRegistration.data
    : [];

const fightersList = rawFighters.map((fighter) => ({
  ...fighter,

  registration_id: Number(
    fighter.registration_id
  ),

  fighter_id: Number(
    fighter.fighter_id
  ),

  division_id:
    fighter.division_id != null
      ? Number(fighter.division_id)
      : null,

  division_weightclass_id:
    fighter.division_weightclass_id != null
      ? Number(fighter.division_weightclass_id)
      : null,

  // Registered weight limit
  weight:
    fighter.weight != null &&
    fighter.weight !== ""
      ? Number(fighter.weight)
      : null,

  // Actual weigh-in weight
  actual_weight:
    fighter.actual_weight != null &&
    fighter.actual_weight !== ""
      ? Number(fighter.actual_weight)
      : null,

  weightclass_name:
    fighter.weightclass_name || "",

  age_division:
    fighter.age_division || "",

  gender:
    fighter.registration_gender ||
    fighter.fighter_gender ||
    "",

  type:
    fighter.type || "main",

  weighed_in:
    Boolean(fighter.weighed_in),
}));

console.log(
  "NORMALIZED BRACKET FIGHTERS:",
  fightersList
);
console.log(
  "FIGHTERS WITH ACTUAL WEIGHT:",
  fightersList.slice(0, 10).map(f => ({
    name: f.name,
    registration_id: f.registration_id,
    actual_weight: f.actual_weight,
    weight: f.weight
  }))
);

setAllFighters(fightersList);

    // ----------------------------------------------------
    // CATEGORIES
    // ----------------------------------------------------

    const rawCategories =
      categoriesRes?.data !== undefined
        ? categoriesRes.data
        : categoriesRes;

    const categoriesList =
      Array.isArray(rawCategories)
        ? rawCategories
        : Array.isArray(rawCategories?.categories)
        ? rawCategories.categories
        : Array.isArray(rawCategories?.data)
        ? rawCategories.data
        : [];

    console.log(
      "BRACKET CATEGORIES:",
      categoriesList
    );

    setBracketCategories(categoriesList);

    // ----------------------------------------------------
    // LOAD EXISTING BRACKETS
    //
    // IMPORTANT:
    // We don't depend on /tournaments/:id/brackets
    // because the actual bracket endpoint is:
    //
    // GET /brackets/:bracketId
    // ----------------------------------------------------

   
    let tournamentBrackets = [];

    try {
      const bracketsRes = await API.get(
        `/tournaments/${tournamentId}/brackets`
      );

      const rawBrackets =
        bracketsRes?.data !== undefined
          ? bracketsRes.data
          : bracketsRes;

      tournamentBrackets =
        Array.isArray(rawBrackets)
          ? rawBrackets
          : Array.isArray(rawBrackets?.brackets)
          ? rawBrackets.brackets
          : Array.isArray(rawBrackets?.data)
          ? rawBrackets.data
          : [];

      console.log(
        "TOURNAMENT BRACKETS:",
        tournamentBrackets
      );
    } catch (err) {
      console.warn(
        "Could not load tournament brackets list:",
        err
      );

      // This should NOT make the entire page blank.
      tournamentBrackets = [];
    }

    // ----------------------------------------------------
// LOAD ALL EXISTING BRACKETS IN PARALLEL
// ----------------------------------------------------

const bracketLoadResults = await Promise.allSettled(
  categoriesList.map(async (category) => {
    const categoryId = String(
      category?.category_id ??
      category?.id ??
      ""
    );

    if (!categoryId) {
      return null;
    }

    const divisionWeightclassId = Number(
      category?.division_weightclass_id
    );

    if (!divisionWeightclassId) {
      return null;
    }

    // Find existing bracket by category first
    const existingBracket = tournamentBrackets.find(
      (bracket) =>
        String(bracket?.category_id) === categoryId ||
        Number(bracket?.division_weightclass_id) ===
          divisionWeightclassId
    );

    if (!existingBracket?.bracket_id) {
      return null;
    }

    const fullRes = await API.get(
      `/brackets/${existingBracket.bracket_id}`
    );

    const fullBracket =
      fullRes?.data !== undefined
        ? fullRes.data
        : fullRes;

    if (!fullBracket) {
      return null;
    }

    return {
      categoryId,
      bracket: fullBracket,
    };
  })
);

const loadedMap = {};

bracketLoadResults.forEach((result) => {
  if (
    result.status === "fulfilled" &&
    result.value?.categoryId &&
    result.value?.bracket
  ) {
    loadedMap[
      result.value.categoryId
    ] = result.value.bracket;
  }

  if (result.status === "rejected") {
    console.warn(
      "Failed loading bracket:",
      result.reason
    );
  }
});

console.log(
  "**LOADED BRACKETS MAP:**",
  loadedMap
);

setSavedBrackets(loadedMap);

    // ----------------------------------------------------
    // AUTO SELECT FIRST EXISTING BRACKET
    // ----------------------------------------------------

    const existingCategoryIds =
      Object.keys(loadedMap);

    if (
      existingCategoryIds.length > 0
    ) {
      const firstCategory =
        existingCategoryIds[0];

      const firstBracket =
        loadedMap[firstCategory];

      setSelectedCategory(
        firstCategory
      );

      setSelectedBracketId(
        firstBracket?.bracket_id || null
      );

      setBracketRounds(
        convertMatchesToRounds(
          firstBracket?.matches || []
        )
      );
    } else {
      setSelectedCategory("");
      setSelectedBracketId(null);
      setBracketRounds([]);
    }

  } catch (err) {
    console.error(
      "Bracket page load failed:",
      err
    );

    setError(
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      "Failed to load tournament bracket data."
    );

  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    loadData();
  }, [tournamentId]);

  // ========================================================
// CATEGORY HELPERS
// ========================================================

const getCategoryById = id => {
  return bracketCategories.find(
    c =>
      String(
        c.category_id ??
        c.id
      ) === String(id)
  );
};

// ========================================================
// CATEGORY → FIGHTERS
// ========================================================

const categoryFighters = useMemo(() => {
  const groups = {};

  // Initialize all categories
  bracketCategories.forEach(category => {
    const categoryId = String(
      category.category_id ??
      category.id ??
      ""
    );

    if (categoryId) {
      groups[categoryId] = [];
    }
  });

  if (
    !Array.isArray(allFighters) ||
    !Array.isArray(bracketCategories)
  ) {
    return groups;
  }

  allFighters.forEach(fighter => {
    // Only exclude inactive registrations
    const active =
      !fighter.status ||
      fighter.status === "active";

    if (!active) {
      return;
    }

    const fighterDivisionWeightclassId = Number(
      fighter.division_weightclass_id
    );

    if (!fighterDivisionWeightclassId) {
      return;
    }

    const category = bracketCategories.find(
      c =>
        Number(c.division_weightclass_id) ===
        fighterDivisionWeightclassId
    );

    if (!category) {
      return;
    }

    const categoryId = String(
      category.category_id ??
      category.id ??
      ""
    );

    if (!categoryId) {
      return;
    }

    if (!groups[categoryId]) {
      groups[categoryId] = [];
    }

    groups[categoryId].push(fighter);
  });

  return groups;

}, [
  allFighters,
  bracketCategories,
]);


// ========================================================
// CATEGORY IDS
// ========================================================

const categories = useMemo(() => {
  return bracketCategories
    .map((category) =>
      String(
        category.category_id ??
        category.id ??
        ""
      )
    )
    .filter(Boolean);
}, [bracketCategories]);

  // ========================================================
  // CATEGORY FILTERS
  // ========================================================

  const getCategoryTypeOrder =
    category => {
      const v =
        String(
          category?.type ||
          ""
        )
          .toLowerCase()
          .trim();

      if (v === "main")
        return 1;

      if (
        v === "altan_garaa" ||
        v === "altan garaa"
      )
        return 2;

      return 99;
    };

  const getCategoryGenderOrder =
    category => {
      const v =
        String(
          category?.gender ||
          ""
        )
          .toLowerCase()
          .trim();

      if (v === "male")
        return 1;

      if (v === "female")
        return 2;

      return 99;
    };

  const getAgeSortValue =
    category => {
      const v =
        String(
          category?.age_division ||
          category?.ageDivision ||
          category?.age_name ||
          category?.label ||
          ""
        );

      const m =
        v.match(
          /(\d+)\s*[-–]\s*(\d+)/
        );

      return m
        ? Number(m[1])
        : 999;
    };

  const getWeightName =
    category =>
      String(
        category?.weightclass_name ||
        category?.weightclass ||
        ""
      ).trim();

  const getWeightSortValue =
    category => {
      const m =
        getWeightName(
          category
        ).match(
          /(\d+(?:\.\d+)?)/
        );

      return m
        ? Number(m[1])
        : 9999;
    };


  const availableTypes =
    useMemo(
      () =>
        [
          ...new Set(
            bracketCategories
              .map(
                c =>
                  String(
                    c?.type ||
                    ""
                  ).trim()
              )
              .filter(Boolean)
          ),
        ].sort(
          (a, b) =>
            getCategoryTypeOrder({
              type: a,
            }) -
            getCategoryTypeOrder({
              type: b,
            })
        ),
      [bracketCategories]
    );

  const availableGenders =
    useMemo(
      () =>
        [
          ...new Set(
            bracketCategories
              .map(
                c =>
                  String(
                    c?.gender ||
                    ""
                  ).trim()
              )
              .filter(Boolean)
          ),
        ].sort(
          (a, b) =>
            getCategoryGenderOrder({
              gender: a,
            }) -
            getCategoryGenderOrder({
              gender: b,
            })
        ),
      [bracketCategories]
    );

  const availableAges =
    useMemo(
      () =>
        [
          ...new Set(
            bracketCategories
              .map(
                c =>
                  String(
                    c?.age_division ||
                    c?.ageDivision ||
                    ""
                  ).trim()
              )
              .filter(Boolean)
          ),
        ].sort(
          (a, b) =>
            getAgeSortValue({
              age_division: a,
            }) -
            getAgeSortValue({
              age_division: b,
            })
        ),
      [bracketCategories]
    );

  const availableWeights =
    useMemo(
      () =>
        [
          ...new Set(
            bracketCategories
              .map(
                getWeightName
              )
              .filter(Boolean)
          ),
        ].sort(
          (a, b) => {
            const ap =
              a.includes("+");

            const bp =
              b.includes("+");

            if (ap !== bp) {
              return ap ? 1 : -1;
            }

            return (
              getWeightSortValue({
                weightclass_name:
                  a,
              }) -
              getWeightSortValue({
                weightclass_name:
                  b,
              })
            );
          }
        ),
      [bracketCategories]
    );

  const filteredCategories =
    useMemo(
      () =>
        categories.filter(
          id => {
            const c =
              getCategoryById(
                id
              );

            if (!c)
              return false;

            const type =
              String(
                c.type || ""
              ).trim();

            const gender =
              String(
                c.gender || ""
              ).trim();

            const age =
              String(
                c.age_division ||
                c.ageDivision ||
                ""
              ).trim();

            const weight =
              getWeightName(c);

            return (
              (!filterType ||
                type ===
                  filterType) &&
              (!filterGender ||
                gender ===
                  filterGender) &&
              (!filterAge ||
                age ===
                  filterAge) &&
              (!filterWeight ||
                weight ===
                  filterWeight)
            );
          }
        ),
      [
        categories,
        bracketCategories,
        filterType,
        filterGender,
        filterAge,
        filterWeight,
      ]
    );

  const getBracketFighterIds = bracket => {
    const ids = new Set();

    (bracket?.matches || []).forEach(match => {
      if (match?.fighter1_id) ids.add(Number(match.fighter1_id));
      if (match?.fighter2_id) ids.add(Number(match.fighter2_id));
    });

    return ids;
  };

  const getBracketStatus = categoryId => {
    const fighters = categoryFighters[categoryId] || [];
    const bracket = savedBrackets[categoryId];

    if (!fighters.length) return "EMPTY";
    if (!bracket) return "NOT GENERATED";

    const currentIds = new Set(
      fighters.map(f => Number(f.fighter_id)).filter(Boolean)
    );
    const bracketIds = getBracketFighterIds(bracket);

    if (
      currentIds.size !== bracketIds.size ||
      [...currentIds].some(id => !bracketIds.has(id))
    ) {
      return "STALE";
    }

    const matches = bracket.matches || [];
    const completed = matches.filter(
      m =>
        m?.winner_id ||
        String(m?.status || "").toLowerCase() === "completed"
    ).length;

    if (matches.length > 0 && completed >= matches.length) return "COMPLETED";
    if (completed > 0) return "IN PROGRESS";
    if (fighters.length === 1) return "SMALL";

    return "READY";
  };

  const getStatusOrder = status => ({
    "STALE": 1,
    "NOT GENERATED": 2,
    "SMALL": 3,
    "READY": 4,
    "IN PROGRESS": 5,
    "COMPLETED": 6,
    "EMPTY": 99,
  }[status] || 50);

  const categoryRows = useMemo(() => {
    return filteredCategories
      .map(id => {
        const category = getCategoryById(id) || {};
        const fighters = categoryFighters[id] || [];
        const bracket = savedBrackets[id];
        const matches = bracket?.matches || [];

        const completedMatches = matches.filter(
          m =>
            m?.winner_id ||
            String(m?.status || "").toLowerCase() === "completed"
        ).length;

        const byeCount = matches.filter(
          m =>
            Number(m?.round) === 1 &&
            Boolean(m?.fighter1_id) !== Boolean(m?.fighter2_id)
        ).length;

        let status = getBracketStatus(id);

        return {
          id,
          category,
          fighters,
          bracket,
          count: fighters.length,
          matches,
          completedMatches,
          byeCount,
          status,
        };
      })
      .filter(row => row.count > 0)
      .filter(row => {
        const q = fighterSearch.trim().toLowerCase();
        if (!q) return true;

        const categoryText = [
          row.category?.label,
          row.category?.type,
          row.category?.gender,
          row.category?.age_division,
          row.category?.ageDivision,
          row.category?.weightclass_name,
        ].filter(Boolean).join(" ").toLowerCase();

        const fighterText = row.fighters.map(f =>
          [
            f.name,
            f.fighter_name,
            f.full_name,
            f.club_name,
            f.club,
          ].filter(Boolean).join(" ")
        ).join(" ").toLowerCase();

        return categoryText.includes(q) || fighterText.includes(q);
      })
      .sort((a, b) => {
        if (sortMode === "most") {
          return b.count - a.count || getStatusOrder(a.status) - getStatusOrder(b.status);
        }

        if (sortMode === "fewest") {
          return a.count - b.count || getStatusOrder(a.status) - getStatusOrder(b.status);
        }

        if (sortMode === "status") {
          return getStatusOrder(a.status) - getStatusOrder(b.status) || b.count - a.count;
        }

        if (sortMode === "completion") {
          const aRatio = a.matches.length ? a.completedMatches / a.matches.length : 0;
          const bRatio = b.matches.length ? b.completedMatches / b.matches.length : 0;
          return bRatio - aRatio || b.count - a.count;
        }

        return (
          getCategoryTypeOrder(a.category) - getCategoryTypeOrder(b.category) ||
          getCategoryGenderOrder(a.category) - getCategoryGenderOrder(b.category) ||
          getAgeSortValue(a.category) - getAgeSortValue(b.category) ||
          getWeightSortValue(a.category) - getWeightSortValue(b.category) ||
          String(a.category?.label || a.id).localeCompare(
            String(b.category?.label || b.id),
            undefined,
            { numeric: true }
          )
        );
      });
  }, [
    filteredCategories,
    bracketCategories,
    categoryFighters,
    savedBrackets,
    fighterSearch,
    sortMode,
  ]);

  const sortedCategories = categoryRows.map(row => row.id);

  // ========================================================
  // SELECT CATEGORY
  // ========================================================

  const handleCategoryChange =
    e => {
      const categoryId =
        e.target.value;

      setSelectedCategory(
        categoryId
      );

      setSelectedFighter(
        null
      );

      setMoveDestination(
        null
      );

      setHasUnsavedChanges(false);

      const bracket =
        savedBrackets[
          categoryId
        ];

      if (bracket) {
        setSelectedBracketId(
          bracket.bracket_id
        );

        setBracketRounds(
          convertMatchesToRounds(
            bracket.matches ||
              []
          )
        );
      } else {
        setSelectedBracketId(
          null
        );

        setBracketRounds([]);
      }
    };

  // ========================================================
  // GENERATE / LOAD
  // ========================================================

  const handleGenerate =
    async () => {
      if (!selectedCategory) {
        return alert(
          "Select a category first."
        );
      }

      const fighters =
        categoryFighters[
          selectedCategory
        ] || [];

      if (!fighters.length) {
        return alert(
          "No fighters in this category."
        );
      }

      const existing =
        savedBrackets[
          selectedCategory
        ];

      if (existing) {
        setSelectedBracketId(
          existing.bracket_id
        );

        setBracketRounds(
          convertMatchesToRounds(
            existing.matches ||
              []
          )
        );

        setSuccessMsg(
          "Existing bracket loaded for editing."
        );

        return;
      }

      try {
        setGenerating(true);
        setError("");
        setSuccessMsg("");

        await API.post(
          `/tournaments/${tournamentId}/brackets/generate`
        );

        await loadData();

        setSuccessMsg(
          "Bracket generated and saved."
        );

      } catch (err) {
        console.error(
          "Generate bracket failed:",
          err
        );

        setError(
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to generate bracket."
        );

      } finally {
        setGenerating(false);
      }
    };

  // ========================================================
  // GENERATE ALL
  // ========================================================

  const handleGenerateAll =
    async () => {
      if (!allCategoryRows.length) {
        return alert(
          "No categories available."
        );
      }

      if (
        !window.confirm(
          `Generate brackets for all ${allCategoryRows.length} active categories? Existing brackets will NOT be deleted.`
        )
      ) {
        return;
      }

      try {
        setGenerating(true);
        setError("");
        setSuccessMsg("");

        await API.post(
          `/tournaments/${tournamentId}/brackets/generate`
        );

        await loadData();

        setSuccessMsg(
          "All brackets are generated/saved."
        );

      } catch (err) {
        console.error(
          err
        );

        setError(
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to generate brackets."
        );

      } finally {
        setGenerating(false);
      }
    };

  // ========================================================
  // SLOT HELPERS
  // ========================================================

  const getSlot = (match, slot) => {
  const prefix =
    slot === 1
      ? "fighter1"
      : "fighter2";

  const fighterId =
    match?.[`${prefix}_id`] || null;

  // Find the complete fighter data from allFighters
  const fullFighter = allFighters.find(
    (f) =>
      Number(f.fighter_id || f.id) ===
      Number(fighterId)
  );

  return {
    id: fighterId,

    name:
      match?.[`${prefix}_name`] ||
      fullFighter?.name ||
      "TBD",

    club:
      match?.[`${prefix}_club`] ||
      fullFighter?.club_name ||
      fullFighter?.club ||
      "",

    clubLogo:
      match?.[`${prefix}_club_logo`] ||
      fullFighter?.club_logo ||
      fullFighter?.clubLogo ||
      "",

    actual_weight:
      fullFighter?.actual_weight != null &&
      fullFighter?.actual_weight !== ""
        ? Number(fullFighter.actual_weight)
        : null,
  };
};

  const setSlot = (
  match,
  slot,
  fighter
) => {

  const prefix =
    slot === 1
      ? "fighter1"
      : "fighter2";

  match[`${prefix}_id`] =
    fighter?.id || null;

  match[`${prefix}_name`] =
    fighter?.id
      ? fighter.name
      : "TBD";

  match[`${prefix}_club`] =
    fighter?.club || "";

  match[`${prefix}_club_logo`] =
    fighter?.clubLogo || "";

  match[`${prefix}_actual_weight`] =
    fighter?.actual_weight != null
      ? Number(fighter.actual_weight)
      : null;
};

  const cloneRounds =
    rounds =>
      rounds.map(
        round =>
          round.map(
            match => ({
              ...match,
            })
          )
      );

  // ========================================================
  // SELECT FIGHTER
  // ========================================================

  const selectFighter =
    (
      roundIndex,
      matchIndex,
      slot
    ) => {
      if (!isAdmin)
        return;

      const fighter =
        getSlot(
          bracketRounds[
            roundIndex
          ]?.[matchIndex],
          slot
        );

      if (!fighter.id)
        return;

      setSelectedFighter({
        roundIndex,
        matchIndex,
        slot,
        ...fighter,
      });

      setMoveDestination(
        null
      );
    };

  // ========================================================
  // DESTINATION
  // ========================================================

  const chooseDestination =
  (
    roundIndex,
    matchIndex,
    slot
  ) => {

    if (!selectedFighter) {
      return;
    }

    // IMPORTANT:
    // Only allow editing Round 1.
    // Later rounds are generated from winners and BYE propagation.
    if (
      selectedFighter.roundIndex !== 0 ||
      roundIndex !== 0
    ) {
      setError(
        "For safety, fighters can only be manually moved inside Round 1."
      );

      return;
    }

    if (
      selectedFighter.roundIndex === roundIndex &&
      selectedFighter.matchIndex === matchIndex &&
      selectedFighter.slot === slot
    ) {
      return;
    }

    setMoveDestination({
      roundIndex,
      matchIndex,
      slot,
    });
  };

  // ========================================================
  // MOVE / SWAP
  // ========================================================

  const commitMove =
    () => {
      if (
        !selectedFighter ||
        !moveDestination
      ) {
        return;
      }

      const rounds =
        cloneRounds(
          bracketRounds
        );

      const sourceMatch =
        rounds[
          selectedFighter
            .roundIndex
        ]?.[
          selectedFighter
            .matchIndex
        ];

      const destinationMatch =
        rounds[
          moveDestination
            .roundIndex
        ]?.[
          moveDestination
            .matchIndex
        ];

      if (
        !sourceMatch ||
        !destinationMatch
      ) {
        return;
      }

      const source =
        getSlot(
          sourceMatch,
          selectedFighter.slot
        );

      const target =
        getSlot(
          destinationMatch,
          moveDestination.slot
        );

      // SWAP
      setSlot(
        sourceMatch,
        selectedFighter.slot,
        target.id
          ? target
          : null
      );

      setSlot(
        destinationMatch,
        moveDestination.slot,
        source
      );

      // Clear winner information.
      // Moving fighters means previous winner state
      // may no longer be valid.
      sourceMatch.winner_id =
        null;

      destinationMatch.winner_id =
        null;

      sourceMatch.status =
        "pending";

      destinationMatch.status =
        "pending";

      setBracketRounds(
        rounds
      );

      setHasUnsavedChanges(true);

      setSelectedFighter(
        null
      );

      setMoveDestination(
        null
      );

      setSuccessMsg(
        "Bracket changed. Press SAVE to permanently save the new positions."
      );
    };


  // ========================================================
  // PAYLOAD
  // ========================================================

  const roundsToPayload =
    rounds =>
      rounds.flatMap(
        round =>
          round.map(
            match => ({
              match_id:
                match.match_id,

              round:
                Number(
                  match.round_number
                ),

              match_index:
                Number(
                  match.match_number
                ),

              fighter1_id:
                match.fighter1_id
                  ? Number(
                      match.fighter1_id
                    )
                  : null,

              fighter2_id:
                match.fighter2_id
                  ? Number(
                      match.fighter2_id
                    )
                  : null,

              winner_id:
                match.winner_id
                  ? Number(
                      match.winner_id
                    )
                  : null,
            })
          )
      );

  // ========================================================
  // SAVE
  // ========================================================

  const handleSave =
    async () => {
      if (
        !selectedBracketId
      ) {
        return alert(
          "No bracket selected."
        );
      }

      try {
        setSaving(true);
        setError("");
        setSuccessMsg("");

        const payload =
          roundsToPayload(
            bracketRounds
          );

        const result =
          await API.put(
            `/brackets/${selectedBracketId}`,
            {
              matches:
                payload,
            }
          );

        const updated =
          result?.data !==
          undefined
            ? result.data
            : result;

        const updatedBracket =
          updated?.bracket ||
          updated;

        // --------------------------------------------------
        // UPDATE LOCAL STATE
        // --------------------------------------------------

        setSavedBrackets(
          prev => ({
            ...prev,
            [selectedCategory]:
              updatedBracket,
          })
        );

        setBracketRounds(
          convertMatchesToRounds(
            updatedBracket?.matches ||
              []
          )
        );

        setHasUnsavedChanges(false);

        setSuccessMsg(
          "✓ Bracket successfully saved to database."
        );

      } catch (err) {
        console.error(
          "Save bracket failed:",
          err
        );

        setError(
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to save bracket."
        );

      } finally {
        setSaving(false);
      }
    };

  // ========================================================
// CLUB LOGO
// ========================================================

const getClubLogoUrl = logo => {
  if (!logo) {
    return "https://warfc.com/api/uploads/clubs/default-club.png";
  }

  if (String(logo).startsWith("http")) {
    return logo;
  }

  const clean = String(logo)
    .replace(/^\/+/, "")
    .replace(/^uploads\/clubs\//, "");

  return `https://warfc.com/api/uploads/clubs/${clean}`;
};

  // ========================================================
  // RENDER SLOT
  // ========================================================

  const renderSlot =
    (
      match,
      roundIndex,
      matchIndex,
      slot,
      isRed
    ) => {
      const fighter =
        getSlot(
          match,
          slot
        );

      const selected =
        selectedFighter &&
        Number(
          selectedFighter.id
        ) ===
          Number(
            fighter.id
          );

      const destination =
        moveDestination &&
        moveDestination.roundIndex ===
          roundIndex &&
        moveDestination.matchIndex ===
          matchIndex &&
        moveDestination.slot ===
          slot;

      return (
        <button
          type="button"
          disabled={
            !fighter.id ||
            !isAdmin
          }
          onClick={() => {
            if (!fighter.id)
              return;

            if (
              selectedFighter
            ) {
              chooseDestination(
                roundIndex,
                matchIndex,
                slot
              );
            } else {
              selectFighter(
                roundIndex,
                matchIndex,
                slot
              );
            }
          }}
          className={`
            w-full
            text-left
            p-3
            rounded-xl
            border
            transition-all

            ${
              selected
                ? "border-[#d4af37] bg-[#d4af37]/15 shadow-[0_0_20px_rgba(212,175,55,0.12)]"
                : ""
            }

            ${
              destination
                ? "border-emerald-500 bg-emerald-500/10"
                : ""
            }

            ${
              !selected &&
              !destination
                ? "border-neutral-800 bg-neutral-900/70"
                : ""
            }

            ${
              fighter.id &&
              isAdmin
                ? "hover:border-[#d4af37]/60 cursor-pointer"
                : "cursor-default opacity-70"
            }
          `}
        >
          <div className="flex items-center gap-3">

            <div
              className={`
                w-9
                h-9
                rounded-full
                border-2
                overflow-hidden
                shrink-0
                flex
                items-center
                justify-center

                ${
                  isRed
                    ? "border-red-500 bg-red-500/10"
                    : "border-blue-500 bg-blue-500/10"
                }
              `}
            >
              {fighter.id ? (
                <img
                  src={getClubLogoUrl(
                    fighter.clubLogo
                  )}
                  alt=""
                  className="w-full h-full object-contain p-1"
                  onError={e => {
                    e.currentTarget.onerror =
                      null;

                    e.currentTarget.src =
                      "https://warfc.com/api/uploads/clubs/default-club.png";
                  }}
                />
              ) : (
                <span className="text-neutral-700 text-xs">
                  —
                </span>
              )}
            </div>

            <div className="min-w-0 flex-1">

              <div className="flex items-center gap-2 min-w-0">
  <div className="text-xs font-black uppercase truncate">
  {fighter.id
    ? fighter.name
    : "TBD / BYE"}
</div>

  {fighter.id &&
    fighter.actual_weight != null && (
      <span className="shrink-0 text-[10px] font-bold text-[#d4af37]">
        {fighter.actual_weight}kg
      </span>
    )}
</div>

              {fighter.club && (
                <div className="text-[10px] text-neutral-500 truncate">
                  {fighter.club}
                </div>
              )}
            </div>

            {selected && (
              <span className="text-[9px] font-black text-[#d4af37]">
                SELECTED
              </span>
            )}

            {destination && (
              <span className="text-[9px] font-black text-emerald-400">
                TARGET
              </span>
            )}

          </div>
        </button>
      );
    };

  const navigateCategory = direction => {
    const currentIndex = categoryRows.findIndex(
      row => String(row.id) === String(selectedCategory)
    );

    if (currentIndex < 0) return;

    const nextIndex = currentIndex + direction;

    if (
      nextIndex < 0 ||
      nextIndex >= categoryRows.length
    ) {
      return;
    }

    handleCategoryChange({
      target: {
        value: String(categoryRows[nextIndex].id),
      },
    });
  };

  const allCategoryRows = useMemo(
    () =>
      categories
        .map(id => {
          const category = getCategoryById(id) || {};
          const fighters = categoryFighters[id] || [];
          const bracket = savedBrackets[id];
          const matches = bracket?.matches || [];

          const completedMatches = matches.filter(
            m =>
              m?.winner_id ||
              String(m?.status || "").toLowerCase() === "completed"
          ).length;

          const byeCount = matches.filter(
            m =>
              Number(m?.round) === 1 &&
              Boolean(m?.fighter1_id) !== Boolean(m?.fighter2_id)
          ).length;

          return {
            id,
            category,
            fighters,
            bracket,
            count: fighters.length,
            matches,
            completedMatches,
            byeCount,
            status: getBracketStatus(id),
          };
        })
        .filter(row => row.count > 0),
    [
      categories,
      categoryFighters,
      savedBrackets,
      bracketCategories,
    ]
  );


  // ========================================================
  // LOADING
  // ========================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white pt-28 flex items-center justify-center">
        <p className="text-[#d4af37] font-black uppercase tracking-widest text-xs animate-pulse">
          Loading tournament brackets...
        </p>
      </div>
    );
  }

  // ========================================================
  // COUNTS / BRACKET HEALTH
  // ========================================================


  const generatedCategoriesCount = Object.keys(savedBrackets).length;

  const mainBracketsCount = Object.keys(savedBrackets).filter(id => {
    const type = String(getCategoryById(id)?.type || "").toLowerCase().trim();
    return type === "main";
  }).length;

  const altanGaraaBracketsCount = Object.keys(savedBrackets).filter(id => {
    const type = String(getCategoryById(id)?.type || "").toLowerCase().trim();
    return type === "altan_garaa" || type === "altan garaa";
  }).length;

  const totalRegisteredFighters = allFighters.filter(
    fighter =>
      !fighter.status ||
      fighter.status === "active"
  ).length;

  const orphanedBracketIds = Object.keys(savedBrackets).filter(
    id => (categoryFighters[id] || []).length === 0
  );

  const bracketedFighterIds = new Set();
  Object.values(savedBrackets).forEach(bracket => {
    getBracketFighterIds(bracket).forEach(id => bracketedFighterIds.add(id));
  });

  const unbracketedFighters = allFighters.filter(fighter => {
    const active = !fighter.status || fighter.status === "active";
    return (
      active &&
      !bracketedFighterIds.has(Number(fighter.fighter_id))
    );
  });

  const readyCount = allCategoryRows.filter(r => r.status === "READY").length;
  const smallCount = allCategoryRows.filter(r => r.status === "SMALL").length;
  const staleCount = allCategoryRows.filter(r => r.status === "STALE").length;
  const notGeneratedCount = allCategoryRows.filter(r => r.status === "NOT GENERATED").length;
  const inProgressCount = allCategoryRows.filter(r => r.status === "IN PROGRESS").length;
  const completedCount = allCategoryRows.filter(r => r.status === "COMPLETED").length;

  const attentionRows = allCategoryRows.filter(row =>
    ["STALE", "NOT GENERATED", "SMALL"].includes(row.status)
  );

  const selectedCategoryRow = allCategoryRows.find(
    row => String(row.id) === String(selectedCategory)
  );

  // ========================================================
  // UI
  // ========================================================

  return (
    <div className="min-h-screen bg-black text-white pt-28 pb-20 px-4">

      <div className="max-w-[1600px] mx-auto">

        {/* ==================================================
            HEADER
        ================================================== */}

        <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between mb-6 gap-4 border-b border-neutral-800 pb-6">

          <div>

            <Link
              to="/tournaments"
              className="text-neutral-500 text-xs font-bold uppercase hover:text-white"
            >
              ← Back
            </Link>

            <h1 className="text-3xl md:text-5xl font-black italic uppercase mt-1">
              ⚡ Bracket Manager
            </h1>

            <p className="text-neutral-400 text-xs font-bold uppercase mt-1 leading-relaxed">
              Fighters:
              {" "}
              <span className="text-[#d4af37]">{totalRegisteredFighters}</span>
              {" "} • {" "}
              <span className="text-emerald-400">{mainBracketsCount} MAIN</span>
              {" "} / {" "}
              <span className="text-amber-400">{altanGaraaBracketsCount} ALTAN GARAA</span>
              {" "} • {" "}
              <span className="text-neutral-300">{generatedCategoriesCount} TOTAL</span>
            </p>

          </div>

          <div className="flex flex-wrap gap-3 w-full xl:w-auto">

            <button
              onClick={resetBrackets}
              disabled={generating || resetting}
              className="
                px-5
                py-3
                rounded-xl
                bg-red-600/10
                border border-red-600/30
                text-red-400
                text-xs
                font-black
                uppercase
                disabled:opacity-40
              "
            >
              {resetting ? "Resetting..." : "🗑 Reset Brackets"}
            </button>

            <button
              onClick={
                handleGenerateAll
              }
              disabled={
                generating
              }
              className="
                flex-1
                px-5
                py-3
                rounded-xl
                bg-amber-500/10
                border
                border-amber-500/30
                text-amber-300
                text-xs
                font-black
                uppercase
                disabled:opacity-40
              "
            >
              {generating
                ? "Generating..."
                : "⚡ Generate Missing Brackets"}
            </button>

            <button
              onClick={
                handleSave
              }
              disabled={
                saving ||
                !selectedBracketId ||
                !bracketRounds.length
              }
              className="
                flex-1
                px-6
                py-3
                rounded-xl
                bg-gradient-to-r
                from-[#d4af37]
                to-amber-500
                text-black
                text-xs
                font-black
                uppercase
                disabled:opacity-40
              "
            >
              {saving
                ? "Saving..."
                : "💾 SAVE BRACKET"}
            </button>

          </div>
        </div>

        {orphanedBracketIds.length > 0 && (
          <div className="mb-6 rounded-2xl bg-red-950/10 border border-red-900/50 p-4">
            <div className="text-red-400 text-xs font-black uppercase">
              ⚠️ {orphanedBracketIds.length} Old Bracket{orphanedBracketIds.length === 1 ? "" : "s"} Need Rebuild
            </div>
            <div className="text-[10px] text-neutral-500 mt-1">
              These generated brackets belong to categories that no longer have active registered fighters.
              This usually happens after a registration category is changed.
            </div>
            <div className="mt-3 text-[10px] text-red-300 font-bold uppercase">
              Use RESET BRACKETS → Generate Missing Brackets to rebuild from current registrations.
            </div>
          </div>
        )}

        {attentionRows.length > 0 && (
          <div className="mb-6 p-5 rounded-2xl bg-red-950/20 border border-red-900/60">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="text-red-400 text-xs font-black uppercase">
                ⚠️ Attention Required
              </div>
              <div className="text-[10px] text-red-300 font-black uppercase">
                {attentionRows.length} categor{attentionRows.length === 1 ? "y" : "ies"}
              </div>
            </div>

            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
              {attentionRows.map(row => (
                <button
                  key={row.id}
                  onClick={() => handleCategoryChange({
                    target: { value: String(row.id) }
                  })}
                  className="text-left p-3 rounded-xl bg-black/40 border border-red-900/40 hover:border-red-500 transition"
                >
                  <div className="text-xs font-black truncate">
                    {row.category?.label || `Category #${row.id}`}
                  </div>
                  <div className="text-[10px] mt-1 font-black uppercase">
                    <span className={
                      row.status === "STALE"
                        ? "text-red-400"
                        : row.status === "SMALL"
                          ? "text-amber-400"
                          : "text-orange-400"
                    }>
                      {row.status}
                    </span>
                    {" • "}
                    <span className="text-neutral-400">
                      {row.count} fighter{row.count === 1 ? "" : "s"}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ==================================================
            FILTERS
        ================================================== */}

        <div className="mb-8 bg-neutral-950 p-4 rounded-xl border border-neutral-800 space-y-4">

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">
                Search Fighter / Club / Category
              </label>
              <input
                type="text"
                value={fighterSearch}
                onChange={e => {
                  setFighterSearch(e.target.value);
                  setSelectedCategory("");
                  setBracketRounds([]);
                }}
                placeholder="Search fighter, club, age, weight..."
                className="
                  w-full p-3 bg-neutral-900 text-white rounded-lg
                  border border-neutral-700 text-xs font-bold outline-none
                  focus:border-[#d4af37]
                "
              />
            </div>

            <div className="min-w-[220px]">
              <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">
                Sort Categories
              </label>
              <select
                value={sortMode}
                onChange={e => setSortMode(e.target.value)}
                className="w-full p-3 bg-neutral-900 text-white rounded-lg border border-neutral-700 text-xs font-bold"
              >
                <option value="default">Default Tournament Order</option>
                <option value="most">Most Fighters</option>
                <option value="fewest">Fewest Fighters</option>
                <option value="status">Attention / Status</option>
                <option value="completion">Match Progress</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">

            <FilterSelect
              label="Type"
              value={
                filterType
              }
              onChange={
                e => {
                  setFilterType(
                    e.target.value
                  );

                  setSelectedCategory(
                    ""
                  );

                  setBracketRounds(
                    []
                  );
                }
              }
              values={
                availableTypes
              }
              labelFn={
                v =>
                  v === "main"
                    ? "Main"
                    : v ===
                      "altan_garaa"
                    ? "Altan Garaa"
                    : v
              }
            />

            <FilterSelect
              label="Gender"
              value={
                filterGender
              }
              onChange={
                e => {
                  setFilterGender(
                    e.target.value
                  );

                  setSelectedCategory(
                    ""
                  );

                  setBracketRounds(
                    []
                  );
                }
              }
              values={
                availableGenders
              }
              labelFn={
                v =>
                  v === "male"
                    ? "Male"
                    : v ===
                      "female"
                    ? "Female"
                    : v
              }
            />

            <FilterSelect
              label="Age Division"
              value={
                filterAge
              }
              onChange={
                e => {
                  setFilterAge(
                    e.target.value
                  );

                  setSelectedCategory(
                    ""
                  );

                  setBracketRounds(
                    []
                  );
                }
              }
              values={
                availableAges
              }
            />

            <FilterSelect
              label="Weight"
              value={
                filterWeight
              }
              onChange={
                e => {
                  setFilterWeight(
                    e.target.value
                  );

                  setSelectedCategory(
                    ""
                  );

                  setBracketRounds(
                    []
                  );
                }
              }
              values={
                availableWeights
              }
            />

          </div>

          <div className="flex flex-col md:flex-row gap-3 items-end">

            <div className="flex-1 w-full">

              <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">
                Category
              </label>

              <select
                value={
                  selectedCategory
                }
                onChange={
                  handleCategoryChange
                }
                className="
                  w-full
                  p-3
                  bg-neutral-900
                  text-white
                  rounded-lg
                  border
                  border-neutral-700
                  text-xs
                  font-bold
                "
              >

                <option value="">
                  -- Select Category --
                </option>

                {categoryRows.map(row => (
                  <option key={row.id} value={row.id}>
                    {row.status === "READY" ||
                    row.status === "IN PROGRESS" ||
                    row.status === "COMPLETED"
                      ? "✓ "
                      : row.status === "STALE"
                        ? "⚠ "
                        : "⏳ "}
                    {row.category?.label || `Category #${row.id}`}
                    {" • "}
                    {row.count}
                    {" • "}
                    {row.status}
                  </option>
                ))}


              </select>
            </div>

            <button
              onClick={() => {
                setFilterType(
                  ""
                );
                setFilterGender(
                  ""
                );
                setFilterAge(
                  ""
                );
                setFilterWeight(
                  ""
                );
                setSelectedCategory(
                  ""
                );
                setBracketRounds(
                  []
                );
              }}
              className="
                px-4
                py-3
                rounded-lg
                bg-neutral-900
                border
                border-neutral-700
                hover:border-[#d4af37]
                text-white
                text-xs
                font-bold
                uppercase
              "
            >
              Reset
            </button>

            <button
              onClick={
                handleGenerate
              }
              disabled={
                generating ||
                !selectedCategory
              }
              className="
                px-5
                py-3
                rounded-lg
                bg-[#d4af37]
                text-black
                text-xs
                font-black
                uppercase
                disabled:opacity-30
              "
            >
              {savedBrackets[
                selectedCategory
              ]
                ? "✏️ EDIT BRACKET"
                : "🎲 GENERATE"}
            </button>

          </div>

        </div>

        {/* ==================================================
            MESSAGES
        ================================================== */}

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-950/40 border border-red-800 text-red-400 text-xs font-bold uppercase">
            {error}
          </div>
        )}

        {successMsg && (
          <div className="mb-6 p-4 rounded-xl bg-emerald-950/40 border border-emerald-800 text-emerald-400 text-xs font-bold uppercase">
            {successMsg}
          </div>
        )}

        {/* ==================================================
            EMPTY
        ================================================== */}

        {!selectedCategory ? (
          <div className="
            border
            border-neutral-900
            bg-neutral-950
            rounded-2xl
            p-16
            text-center
          ">
            <p className="text-[#d4af37] font-black uppercase">
              Select a category
            </p>
          </div>
        ) : !bracketRounds.length ? (
          <div className="
            border
            border-neutral-900
            bg-neutral-950
            rounded-2xl
            p-16
            text-center
          ">

            <p className="text-neutral-300 text-sm font-bold">
              No saved bracket for this category.
            </p>

            <button
              onClick={
                handleGenerate
              }
              className="
                mt-5
                px-6
                py-3
                rounded-xl
                bg-[#d4af37]
                text-black
                text-xs
                font-black
                uppercase
              "
            >
              🎲 Generate Bracket
            </button>
          </div>
        ) : (
          <div>

            {selectedCategoryRow && (
              <div className="mb-5 p-5 rounded-2xl bg-neutral-950 border border-neutral-800">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div>
                    <div className="text-[10px] text-neutral-600 font-black uppercase tracking-widest">
                      Current Bracket Category
                    </div>
                    <div className="text-lg md:text-2xl font-black uppercase mt-1">
                      {String(selectedCategoryRow.category?.type || "").toLowerCase() === "main"
                        ? "MAIN"
                        : "ALTAN GARAA"}
                      {" • "}
                      {String(selectedCategoryRow.category?.gender || "").toUpperCase()}
                      {" • "}
                      {selectedCategoryRow.category?.age_division ||
                        selectedCategoryRow.category?.ageDivision ||
                        ""}
                      {" • "}
                      {selectedCategoryRow.category?.weightclass_name || ""}
                    </div>
                    <div className="text-[10px] text-neutral-500 font-bold uppercase mt-2">
                      {selectedCategoryRow.count} FIGHTERS
                      {" • "}
                      {selectedCategoryRow.matches.length} MATCHES
                      {" • "}
                      {selectedCategoryRow.bracket?.bracket_id
                        ? `BRACKET #${selectedCategoryRow.bracket.bracket_id}`
                        : "NO BRACKET"}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-2 rounded-lg bg-neutral-900 border border-neutral-800 text-[10px] font-black uppercase">
                      {selectedCategoryRow.status}
                    </span>
                    {selectedCategoryRow.matches.length > 0 && (
                      <span className="px-3 py-2 rounded-lg bg-neutral-900 border border-neutral-800 text-[10px] text-neutral-400 font-black uppercase">
                        {selectedCategoryRow.completedMatches}/
                        {selectedCategoryRow.matches.length} COMPLETED
                      </span>
                    )}

                    <span className="px-3 py-2 rounded-lg bg-neutral-900 border border-neutral-800 text-[10px] text-neutral-400 font-black uppercase">
                      {selectedCategoryRow.byeCount} BYE
                      {selectedCategoryRow.byeCount === 1 ? "" : "S"}
                    </span>

                    {hasUnsavedChanges && (
                      <span className="px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] font-black uppercase">
                        ● UNSAVED CHANGES
                      </span>
                    )}
                  </div>
                </div>

                {selectedCategoryRow.status === "STALE" && (
                  <div className="mt-4 p-3 rounded-xl bg-red-500/5 border border-red-500/20 text-[10px] text-red-300 font-bold uppercase">
                    ⚠ Registration data changed after this bracket was generated.
                    Reset brackets and generate again to rebuild from current registrations.
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-neutral-800 flex items-center justify-between gap-3">
                  <button
                    onClick={() => navigateCategory(-1)}
                    disabled={
                      categoryRows.findIndex(
                        row => String(row.id) === String(selectedCategory)
                      ) <= 0
                    }
                    className="px-4 py-2 rounded-lg bg-neutral-900 border border-neutral-700 text-[10px] font-black uppercase disabled:opacity-30"
                  >
                    ← Previous
                  </button>

                  <div className="text-[9px] text-neutral-600 font-black uppercase">
                    {categoryRows.findIndex(
                      row => String(row.id) === String(selectedCategory)
                    ) + 1}
                    {" / "}
                    {categoryRows.length}
                  </div>

                  <button
                    onClick={() => navigateCategory(1)}
                    disabled={
                      categoryRows.findIndex(
                        row => String(row.id) === String(selectedCategory)
                      ) < 0 ||
                      categoryRows.findIndex(
                        row => String(row.id) === String(selectedCategory)
                      ) >= categoryRows.length - 1
                    }
                    className="px-4 py-2 rounded-lg bg-neutral-900 border border-neutral-700 text-[10px] font-black uppercase disabled:opacity-30"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}

            {/* ==================================================
                EDITOR INFO
            ================================================== */}

            <div className="
              mb-5
              p-4
              rounded-2xl
              bg-[#d4af37]/5
              border
              border-[#d4af37]/20
            ">

              <div className="text-[#d4af37] text-xs font-black uppercase">
                🛠 Persistent Bracket Editor
              </div>

              <div className="text-neutral-400 text-[11px] mt-2">
                This bracket is loaded from the database.
                Select a fighter → select another slot →
                MOVE / SWAP → SAVE.
                Changes remain after leaving and reopening this page.
              </div>

              {selectedBracketId && (
                <div className="mt-2 text-[10px] text-neutral-600 uppercase">
                  Bracket ID:
                  {" "}
                  {selectedBracketId}
                </div>
              )}

            </div>

            {/* ==================================================
                SELECTED FIGHTER
            ================================================== */}

            {selectedFighter && (
              <div className="
                mb-6
                p-5
                rounded-2xl
                bg-neutral-950
                border
                border-[#d4af37]/40
              ">

                <div className="
                  flex
                  flex-col
                  xl:flex-row
                  gap-4
                  xl:items-center
                  justify-between
                ">

                  <div>

                    <div className="text-[10px] font-black uppercase text-[#d4af37]">
                      Selected Fighter
                    </div>

                    <div className="text-lg font-black uppercase mt-1">
                      {selectedFighter.name}
                    </div>

                    <div className="text-xs text-neutral-500">
                      {selectedFighter.club}
                    </div>

                    <div className="text-[10px] text-neutral-600 uppercase mt-1">
                      Round{" "}
                      {selectedFighter.roundIndex +
                        1}
                      {" • "}
                      Match{" "}
                      {selectedFighter.matchIndex +
                        1}
                      {" • "}
                      {selectedFighter.slot ===
                      1
                        ? "RED"
                        : "BLUE"}
                    </div>

                  </div>

                  <div className="flex gap-2">

                    <button
                      onClick={() => {
                        setSelectedFighter(
                          null
                        );

                        setMoveDestination(
                          null
                        );
                      }}
                      className="
                        px-4
                        py-2
                        rounded-lg
                        bg-neutral-900
                        border
                        border-neutral-700
                        text-neutral-300
                        text-[10px]
                        font-black
                        uppercase
                      "
                    >
                      Cancel
                    </button>

                    {moveDestination && (
                      <button
                        onClick={
                          commitMove
                        }
                        className="
                          px-5
                          py-2
                          rounded-lg
                          bg-[#d4af37]
                          text-black
                          text-[10px]
                          font-black
                          uppercase
                        "
                      >
                        ✓ MOVE / SWAP
                      </button>
                    )}

                  </div>
                </div>



              </div>
            )}

            {/* ==================================================
                BRACKET
            ================================================== */}

            <div className="
              flex
              gap-6
              overflow-x-auto
              pb-8
            ">

              {bracketRounds.map(
                (
                  round,
                  roundIndex
                ) => (
                  <div
                    key={
                      roundIndex
                    }
                    className="
                      flex
                      flex-col
                      gap-4
                      min-w-[300px]
                    "
                  >

                    <div className="
                      text-center
                      font-black
                      uppercase
                      text-xs
                      tracking-widest
                      text-[#d4af37]
                      bg-neutral-900
                      py-3
                      rounded-lg
                      border
                      border-neutral-800
                    ">
                      {roundIndex ===
                      bracketRounds.length -
                        1
                        ? "FINAL"
                        : `ROUND ${
                            roundIndex +
                            1
                          }`}
                    </div>

                    {round.map(
                      (
                        match,
                        matchIndex
                      ) => (
                        <div
                          key={
                            match.match_id ||
                            `${roundIndex}-${matchIndex}`
                          }
                          className="
                            bg-neutral-950
                            border
                            border-neutral-800
                            rounded-xl
                            p-3
                            space-y-2
                          "
                        >

                          <div className="flex justify-between mb-1">

                            <span className="text-[9px] text-neutral-600 uppercase font-black">
                              Match{" "}
                              {match.match_number}
                            </span>

                            {match.winner_id && (
                              <span className="text-[9px] text-emerald-500 uppercase font-black">
                                ✓ ADVANCED
                              </span>
                            )}

                          </div>

                          {renderSlot(
                            match,
                            roundIndex,
                            matchIndex,
                            1,
                            true
                          )}

                          {renderSlot(
                            match,
                            roundIndex,
                            matchIndex,
                            2,
                            false
                          )}

                        </div>
                      )
                    )}

                  </div>
                )
              )}

            </div>

            {/* ==================================================
                SAVE
            ================================================== */}

            <div className="sticky bottom-4 z-40 mt-8 flex justify-center">

              <button
                onClick={
                  handleSave
                }
                disabled={
                  saving ||
                  !selectedBracketId
                }
                className="
                  px-10
                  py-4
                  rounded-2xl
                  bg-gradient-to-r
                  from-[#d4af37]
                  to-amber-500
                  text-black
                  text-xs
                  font-black
                  uppercase
                  tracking-widest
                  shadow-[0_10px_40px_rgba(212,175,55,0.25)]
                  disabled:opacity-40
                "
              >
                {saving
                  ? "SAVING..."
                  : "💾 SAVE BRACKET"}
              </button>

            </div>

          </div>
        )}

      </div>
    </div>
  );
}

// ==========================================================
// FILTER SELECT
// ==========================================================

function FilterSelect({
  label,
  value,
  onChange,
  values,
  labelFn,
}) {
  return (
    <div>

      <label className="
        block
        text-[10px]
        font-black
        uppercase
        tracking-widest
        text-neutral-500
        mb-2
      ">
        {label}
      </label>

      <select
        value={value}
        onChange={onChange}
        className="
          w-full
          p-3
          bg-neutral-900
          text-white
          rounded-lg
          border
          border-neutral-700
          text-xs
          font-bold
        "
      >

        <option value="">
          All {label}
        </option>

        {values.map(
          value => (
            <option
              key={value}
              value={value}
            >
              {labelFn
                ? labelFn(
                    value
                  )
                : value}
            </option>
          )
        )}

      </select>

    </div>
  );
}
